import { z } from "zod";
import {
  ApiErrorSchema,
  AssetSchema,
  AttachmentSchema,
  ChapterContentSchema,
  ChapterSchema,
  CommentReplySchema,
  CommentSortSchema,
  CommentThreadSchema,
  CompleteChapterUploadResponseSchema,
  CreateChapterUploadRequestSchema,
  CreateCommentReplyRequestSchema,
  CreateDiceRollRequestSchema,
  CreateDocumentChapterRequestSchema,
  CreateSuggestionBatchRequestSchema,
  CreateSuggestionRequestSchema,
  CreateTagRequestSchema,
  CursorQuerySchema,
  DeleteDocumentChapterResponseSchema,
  DeleteDocumentAccessResponseSchema,
  DocumentAccessEntrySchema,
  DocumentAccessSchema,
  DocumentTagsSchema,
  ForumSessionSchema,
  DiceRollSchema,
  DocumentEnvelopeSchema,
  DocumentListItemSchema,
  EmojiCatalogSchema,
  EmojiImageParamsSchema,
  EntityIdSchema,
  MentionSearchResultSchema,
  PollSchema,
  PollVotePageSchema,
  PurchaseAttachmentResponseSchema,
  ResolveMentionRequestSchema,
  ResolveMentionResponseSchema,
  ResolveReplyGateRequestSchema,
  ResolveReplyGateResponseSchema,
  RevisionPageSchema,
  ServerTagListSchema,
  RevisionQuerySchema,
  RevisionSnapshotQuerySchema,
  ReviewSuggestionBatchRequestSchema,
  ReviewSuggestionRequestSchema,
  RollbackDocumentRequestSchema,
  SaveNovelChapterRequestSchema,
  SaveNovelChapterResponseSchema,
  SaveNovelChaptersBatchRequestSchema,
  SaveNovelChaptersBatchResponseSchema,
  StageNovelChapterReorderRequestSchema,
  StageNovelChapterReorderResponseSchema,
  StageChapterUploadBatchRequestSchema,
  StageChapterUploadBatchResponseSchema,
  ChapterUploadSessionSchema,
  SubmitPollVoteRequestSchema,
  SuggestionBatchSchema,
  SuggestionSchema,
  SyncNovelChaptersRequestSchema,
  SyncNovelChaptersResponseSchema,
  TagSchema,
  UpdateDocumentChapterRequestSchema,
  UpdateDocumentRequestSchema,
  UpdateDocumentVisibilityRequestSchema,
  UpsertDocumentAccessRequestSchema,
  UpdateDocumentStepsRequestSchema,
  UpdateDocumentTagsRequestSchema,
  UpdateTagRequestSchema,
  VoteCommentRequestSchema,
} from "./schemas.js";

/** 契约使用的 HTTP 方法。 */
export type ContractMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** 单个状态码的响应契约。 */
export interface ContractResponse {
  /** 当前状态码的人类可读语义，会写入 OpenAPI responses。 */
  description: string;
  /** 响应体的运行时 Zod schema。 */
  schema: z.ZodType;
}

/** REST 路由的单一来源元数据。 */
export interface ContractRoute {
  /** 跨服务和生成客户端保持稳定的操作名。 */
  operationId: string;
  /** HTTP 方法。 */
  method: ContractMethod;
  /** 冒号风格路径，其中动态段使用 `:name`（Hono 与其一致）。 */
  path: string;
  /** OpenAPI 列表中的短标题。 */
  summary: string;
  /** 行为、权限和失败语义的完整说明。 */
  description: string;
  /** OpenAPI 分组标签。 */
  tags: string[];
  /** 路径参数 schema。 */
  params?: z.ZodType;
  /** 查询参数 schema。 */
  query?: z.ZodType;
  /** JSON 请求体 schema；multipart 路由由生成器单独处理。 */
  body?: z.ZodType;
  /** 状态码到响应契约的映射。 */
  responses: Record<number, ContractResponse>;
  /** 实现状态用于区分已上线能力和仅规划契约。 */
  implementationStatus?: "implemented" | "planned";
}

const documentParams = z
  .object({ documentId: EntityIdSchema.describe("文档稳定 ID，例如 demo-post") })
  .strict();
const revisionParams = z
  .object({
    documentId: EntityIdSchema.describe("文档稳定 ID，例如 demo-post"),
    revision: z.string().regex(/^\d+$/).describe("不可变 revision 编号"),
  })
  .strict();
const revisionQuery = RevisionQuerySchema;
const assetParams = z
  .object({ assetId: EntityIdSchema.describe("上传后返回的图片资产 ID") })
  .strict();
const diceParams = z
  .object({ rollId: EntityIdSchema.describe("首次投掷或重投生成的稳定 rollId") })
  .strict();
const threadParams = z.object({ documentId: EntityIdSchema, anchorId: EntityIdSchema }).strict();
const commentQuery = CursorQuerySchema.extend({
  sort: CommentSortSchema.default("score"),
}).strict();
const replyParams = z.object({ replyId: EntityIdSchema }).strict();
const suggestionParams = z.object({ suggestionId: EntityIdSchema }).strict();
const suggestionBatchParams = z.object({ batchId: EntityIdSchema }).strict();
const userSearchQuery = z
  .object({ q: z.string().max(80).default(""), friendsOnly: z.coerce.boolean().default(false) })
  .strict();
const attachmentParams = z.object({ attachmentId: EntityIdSchema }).strict();
const pollParams = z.object({ pollId: EntityIdSchema }).strict();
const novelParams = z
  .object({ novelId: EntityIdSchema.describe("章节所属的文档/小说 ID") })
  .strict();
const novelChapterParams = z
  .object({ novelId: EntityIdSchema, chapterId: EntityIdSchema })
  .strict();
const novelUploadParams = z.object({ novelId: EntityIdSchema, uploadId: EntityIdSchema }).strict();
const documentChapterParams = z
  .object({ documentId: EntityIdSchema, chapterId: EntityIdSchema })
  .strict();
const documentQuery = z.object({ documentId: EntityIdSchema }).strict();
const documentAccessUserParams = z
  .object({ documentId: EntityIdSchema, userId: EntityIdSchema })
  .strict();
const tagParams = z.object({ tagId: EntityIdSchema }).strict();

/** 全部 REST 契约；OpenAPI 文档与客户端均由此生成。 */
export const contractRoutes: readonly ContractRoute[] = [
  {
    operationId: "listDocuments",
    method: "GET",
    path: "/api/documents",
    tags: ["文档"],
    summary: "读取可选文章",
    description: "仅登录用户可用；返回可读取文章并标记当前身份是否有编辑权。",
    responses: {
      200: {
        description: "按更新时间倒序的文章摘要。",
        schema: z.object({ items: z.array(DocumentListItemSchema) }).strict(),
      },
      401: { description: "游客不能读取服务器文章列表。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getDocument",
    method: "GET",
    path: "/api/documents/:documentId",
    tags: ["文档"],
    summary: "读取当前文档",
    description: "仅登录用户读取最新不可变修订；非编辑者看不到隐藏章节。",
    params: documentParams,
    responses: {
      200: {
        description: "当前文档，例如 revision 为 1 的 demo-post。",
        schema: DocumentEnvelopeSchema,
      },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getDocumentAccess",
    method: "GET",
    path: "/api/documents/:documentId/access",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "读取文章访问设置",
    description: "仅文档 owner、ACL admin 或 moderator 可读取 owner、公开状态和完整协作者列表。",
    params: documentParams,
    responses: {
      200: { description: "文章访问设置。", schema: DocumentAccessSchema },
      403: { description: "文章可读，但当前身份无权管理访问设置。", schema: ApiErrorSchema },
      404: { description: "文档不存在或当前身份不可读。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "updateDocumentVisibility",
    method: "PATCH",
    path: "/api/documents/:documentId/access",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "修改文章公开状态",
    description: "仅 owner、ACL admin 或 moderator 可用；不创建正文修订。",
    params: documentParams,
    body: UpdateDocumentVisibilityRequestSchema,
    responses: {
      200: { description: "更新后的文章访问设置。", schema: DocumentAccessSchema },
      403: { description: "文章可读，但当前身份无权管理访问设置。", schema: ApiErrorSchema },
      404: { description: "文档不存在或当前身份不可读。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "upsertDocumentAccess",
    method: "PUT",
    path: "/api/documents/:documentId/access/users/:userId",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "新增或更新文章协作者",
    description:
      "仅 owner、ACL admin 或 moderator 可用；owner 权限不可修改，read/edit/admin 按用户原子覆盖。",
    params: documentAccessUserParams,
    body: UpsertDocumentAccessRequestSchema,
    responses: {
      200: { description: "更新后的 ACL 用户条目。", schema: DocumentAccessEntrySchema },
      403: { description: "文章可读，但当前身份无权管理 ACL。", schema: ApiErrorSchema },
      404: { description: "文档、目标用户不存在或当前身份不可读。", schema: ApiErrorSchema },
      409: { description: "不能修改文档 owner 的 ACL。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "deleteDocumentAccess",
    method: "DELETE",
    path: "/api/documents/:documentId/access/users/:userId",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "撤销文章协作者权限",
    description: "仅 owner、ACL admin 或 moderator 可用；删除不存在的非 owner 条目仍成功。",
    params: documentAccessUserParams,
    responses: {
      200: {
        description: "幂等删除结果。",
        schema: DeleteDocumentAccessResponseSchema,
      },
      403: { description: "文章可读，但当前身份无权管理 ACL。", schema: ApiErrorSchema },
      404: { description: "文档不存在或当前身份不可读。", schema: ApiErrorSchema },
      409: { description: "不能删除文档 owner 的 ACL。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "updateDocument",
    method: "PUT",
    path: "/api/documents/:documentId",
    tags: ["文档"],
    summary: "保存或首次创建文档",
    description:
      "需要 author 或 moderator。文档不存在且 baseRevision=0 时创建文档、owner ACL、章节和首个修订；其他保存使用乐观并发，相同 clientMutationId 重试不会重复建版。",
    params: documentParams,
    body: UpdateDocumentRequestSchema,
    responses: {
      200: { description: "幂等重试命中的既有修订。", schema: DocumentEnvelopeSchema },
      201: { description: "新建的不可变修订。", schema: DocumentEnvelopeSchema },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      409: {
        description: "当前 revision 与 baseRevision 冲突，details.currentRevision 可用于刷新。",
        schema: ApiErrorSchema,
      },
      422: { description: "正文包含非法节点、属性或 URL。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "updateDocumentChapter",
    method: "PATCH",
    path: "/api/documents/:documentId/chapters/:chapterId",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "更新章节目录行（隐藏/恢复）",
    description: "需要 author 或 moderator。隐藏的章节读者不可读；作者写完取消隐藏后恢复可读。",
    params: documentChapterParams,
    body: UpdateDocumentChapterRequestSchema,
    responses: {
      200: { description: "更新后的章节目录行。", schema: ChapterSchema },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      404: { description: "文档或章节不存在。", schema: ApiErrorSchema },
      422: { description: "请求字段非法。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "deleteDocumentChapter",
    method: "DELETE",
    path: "/api/documents/:documentId/chapters/:chapterId",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "删除章节目录中的章节",
    description:
      "需要 author 或 moderator。编辑器「删除章节」移除正文后再调用本接口删除对应目录行（幂等：不存在时返回 deleted = false）；历史修订与章节独立版本号不受影响，关联校订建议解除章节归属但不删除。",
    params: documentChapterParams,
    responses: {
      200: {
        description: "删除结果；deleted = false 表示目录中本就没有该章。",
        schema: DeleteDocumentChapterResponseSchema,
      },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createDocumentChapter",
    method: "POST",
    path: "/api/documents/:documentId/chapters",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "注册正文中出现的新章节",
    description:
      "需要 author 或 moderator。编辑器的「新增章节」只修改正文；保存前客户端必须把正文中服务器目录缺失的新章节注册进来。章节身份由客户端以 chapter_<uuid> 铸造，省略时服务端兜底铸造；重复注册同一 ID 幂等返回同一行，顺序只由 order 字段表达。",
    params: documentParams,
    body: CreateDocumentChapterRequestSchema,
    responses: {
      200: { description: "重复注册命中的已有章节行。", schema: ChapterSchema },
      201: { description: "新建的章节行，客户端应把该 id 同步回本地目录。", schema: ChapterSchema },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
      422: { description: "标题或顺序非法。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "updateDocumentSteps",
    method: "PATCH",
    path: "/api/documents/:documentId/steps",
    tags: ["文档"],
    summary: "使用 ProseMirror 增量步骤更新文档",
    description:
      "需要文档 edit 权限。客户端提交 transaction steps，服务端基于当前 revision 应用 steps 并创建新修订。",
    params: documentParams,
    body: UpdateDocumentStepsRequestSchema,
    responses: {
      200: { description: "幂等重试命中的既有修订。", schema: DocumentEnvelopeSchema },
      201: { description: "新建的不可变修订。", schema: DocumentEnvelopeSchema },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      409: { description: "当前 revision 与 baseRevision 冲突。", schema: ApiErrorSchema },
      422: { description: "steps 无法应用到当前文档或包含非法结构。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "syncNovelChapters",
    method: "POST",
    path: "/api/forum/novels/:novelId/chapters/sync",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "对比章节内容哈希",
    description:
      "需要文档 owner、ACL editor 或 moderator。客户端提交本地章节清单（含 SHA-256 哈希），服务端对比已存哈希，返回需要上传的章节与已存在（无需上传）的章节 ID。",
    params: novelParams,
    body: SyncNovelChaptersRequestSchema,
    responses: {
      200: { description: "需要更新与已存在的章节 ID。", schema: SyncNovelChaptersResponseSchema },
    },
  },
  {
    operationId: "getNovelChapter",
    method: "GET",
    path: "/api/forum/novels/:novelId/chapters/:chapterId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取单个已上传章节",
    description: "按文章与章节 ID 返回分章正文；隐藏章节仅编辑者可读。",
    params: novelChapterParams,
    responses: {
      200: { description: "章节目录元数据与正文。", schema: ChapterContentSchema },
      404: { description: "文章、章节不存在或当前身份不可读。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "saveNovelChapter",
    method: "PUT",
    path: "/api/forum/novels/:novelId/chapters/:chapterId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "保存单个章节",
    description:
      "需要 author 或 moderator。正文经过与文档相同的白名单校验；该章节 baseRevision 过期返回 409。",
    params: novelChapterParams,
    body: SaveNovelChapterRequestSchema,
    responses: {
      201: { description: "保存后的章节版本摘要。", schema: SaveNovelChapterResponseSchema },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      409: { description: "章节已被其他修改更新。", schema: ApiErrorSchema },
      422: { description: "章节内容或字段非法。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "saveNovelChaptersBatch",
    method: "POST",
    path: "/api/forum/novels/:novelId/chapters/batch",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "批量保存章节正文（每批最多 20 章）",
    description:
      "需要 author 或 moderator。章节身份由文章 ID 与章节 ID 共同确定。整批先读取当前文章内相关章节的 revision、order 与 content_hash 完成预校验：目标 order 被批外章节占用、批内目标 order 重复或任一 baseRevision 过期时整批返回 409（details.chapterId 指出具体章节），正文不发生部分提交。已存在记录 content_hash 与请求 hash 一致时返回 unchanged 与当前 revision，因此上次响应丢失后重试不会再次递增版本。正文继续执行标准 Tiptap 清洗与 longTextBlock 转换保护。路由 body 上限约 5 MiB，超出返回 413。",
    params: novelParams,
    body: SaveNovelChaptersBatchRequestSchema,
    responses: {
      200: {
        description: "与请求顺序一致的单章保存结果。",
        schema: SaveNovelChaptersBatchResponseSchema,
      },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      409: {
        description: "批内任一章节存在 order 或 baseRevision 冲突；未发生部分提交。",
        schema: ApiErrorSchema,
      },
      413: { description: "序列化请求体超过约 5 MiB 上限。", schema: ApiErrorSchema },
      422: { description: "章节数超过 20 或章节字段非法。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createChapterUpload",
    method: "POST",
    path: "/api/forum/novels/:novelId/chapter-uploads",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "创建或恢复整本章节上传会话",
    description:
      "冻结章节总数与有序清单哈希；相同文章和清单的未完成会话幂等复用。暂存期间不修改线上章节。",
    params: novelParams,
    body: CreateChapterUploadRequestSchema,
    responses: {
      200: { description: "可续传的上传会话。", schema: ChapterUploadSessionSchema },
      403: { description: "无编辑权限。", schema: ApiErrorSchema },
      409: { description: "上传清单与既有会话冲突。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "stageChapterUploadBatch",
    method: "PUT",
    path: "/api/forum/novels/:novelId/chapter-uploads/:uploadId/batch",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "暂存整本上传的一个正文批次",
    description:
      "每批最多 20 章，只写上传会话暂存区；ID、order、hash 或基线版本冲突时整批拒绝，线上目录不变。",
    params: novelUploadParams,
    body: StageChapterUploadBatchRequestSchema,
    responses: {
      200: { description: "暂存结果。", schema: StageChapterUploadBatchResponseSchema },
      403: { description: "无编辑权限。", schema: ApiErrorSchema },
      409: { description: "会话、顺序或版本冲突。", schema: ApiErrorSchema },
      413: { description: "批次过大。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "completeChapterUpload",
    method: "POST",
    path: "/api/forum/novels/:novelId/chapter-uploads/:uploadId/complete",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "验收并原子发布完整章节集",
    description:
      "服务端复算有序清单哈希并校验 0..N-1、数量、正文和基线版本；全部通过后才在一个事务内替换线上章节。",
    params: novelUploadParams,
    responses: {
      200: { description: "原子发布结果。", schema: CompleteChapterUploadResponseSchema },
      403: { description: "无编辑权限。", schema: ApiErrorSchema },
      409: { description: "清单不完整、顺序错误或基线版本变化。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "stageNovelChapterReorder",
    method: "POST",
    path: "/api/forum/novels/:novelId/chapters/reorder-stage",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "换序暂存（每批最多 40 项，不发送正文）",
    description:
      "需要 author 或 moderator。先把当前文章的所有移动章节按批放到文章内唯一临时 order，再执行最终正文批次，避免中途占用其他章节的目标位置。当前顺序已经等于临时顺序时幂等返回 unchanged，不重复递增 revision；上次响应丢失后的重试同样返回当前 revision。目标临时 order 被批外章节占用、批内临时 order 重复或 baseRevision 过期时整批返回 409（details.chapterId 指出具体章节）。",
    params: novelParams,
    body: StageNovelChapterReorderRequestSchema,
    responses: {
      200: {
        description: "与请求顺序一致的单章暂存结果。",
        schema: StageNovelChapterReorderResponseSchema,
      },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      409: {
        description: "批内任一章节存在临时 order 或 baseRevision 冲突；未发生部分提交。",
        schema: ApiErrorSchema,
      },
      413: { description: "序列化请求体超过约 5 MiB 上限。", schema: ApiErrorSchema },
      422: { description: "章节数超过 40 或字段非法。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "listRevisions",
    method: "GET",
    path: "/api/documents/:documentId/revisions",
    tags: ["文档"],
    summary: "分页读取版本历史",
    description:
      "带 chapterId 时直接读章节版本账本，按该章节自己的内容版本倒序返回；cursor 使用上一页最后一个章节版本。" +
      "origin 表明该版本是来自整篇不可变快照还是独立章节正文快照。",

    params: documentParams,
    query: revisionQuery,
    responses: {
      200: { description: "不可变版本摘要页。", schema: RevisionPageSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getRevision",
    method: "GET",
    path: "/api/documents/:documentId/revisions/:revision",
    tags: ["文档"],
    summary: "读取指定历史版本",
    description:
      "带 chapterId 时，路径中的 revision 是该章节自己的版本号；当该版本来自整篇不可变快照时返回该快照，" +
      "否则返回账本里保存的独立章节正文快照。两种情况都可用于只读比较。",
    params: revisionParams,
    query: RevisionSnapshotQuerySchema,
    responses: {
      200: { description: "指定历史 revision 的完整快照。", schema: DocumentEnvelopeSchema },
      404: { description: "文档或版本不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "rollbackDocument",
    method: "POST",
    path: "/api/documents/:documentId/rollback",
    tags: ["文档"],
    summary: "回退当前章节到指定版本",
    description:
      "需要 author 或 moderator。只替换目标章节，并为该章节创建下一个版本；其他章节保持不变。",
    params: documentParams,
    body: RollbackDocumentRequestSchema,
    responses: {
      200: { description: "幂等重试命中的回滚结果。", schema: DocumentEnvelopeSchema },
      201: { description: "新创建的回滚修订。", schema: DocumentEnvelopeSchema },
      403: { description: "当前身份无回滚权限。", schema: ApiErrorSchema },
      404: { description: "文档或目标修订不存在。", schema: ApiErrorSchema },
      409: { description: "baseRevision 已过期。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "uploadAsset",
    method: "POST",
    path: "/api/assets",
    tags: ["图片"],
    summary: "上传本地图片",
    description:
      "multipart/form-data 的 file 字段；允许 PNG/JPEG/GIF/WebP，最多 8 MiB。所有已登录论坛身份可上传。",
    responses: {
      201: { description: "可直接用于 richImage 节点的资产 URL。", schema: AssetSchema },
      413: { description: "图片超过大小限制。", schema: ApiErrorSchema },
      415: { description: "MIME 或文件签名不是受支持图片。", schema: ApiErrorSchema },
      422: { description: "缺少 file 字段。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "readAsset",
    method: "GET",
    path: "/api/assets/:assetId",
    tags: ["图片"],
    summary: "读取资产二进制",
    description:
      "按资产 ID 返回图片或附件。Worker 支持 ETag、Range；付费附件仅作者、版主或已购买用户可读。",
    params: assetParams,
    responses: {
      200: { description: "完整二进制；Content-Type 来自保存的白名单 MIME。", schema: z.any() },
      206: { description: "满足 Range 请求的部分二进制。", schema: z.any() },
      304: { description: "ETag 未变化，无响应体。", schema: z.any() },
      401: { description: "受保护附件要求登录。", schema: ApiErrorSchema },
      403: { description: "当前身份未购买该附件。", schema: ApiErrorSchema },
      404: { description: "资产不存在。", schema: ApiErrorSchema },
      416: { description: "Range 超出资产范围。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "listEmojiCatalog",
    method: "GET",
    path: "/api/emoji",
    tags: ["表情"],
    implementationStatus: "implemented",
    summary: "读取站点表情目录",
    description:
      "返回全站内置分组与全部表情条目（纯文本 Unicode 表情、颜文字与自定义表情包）。目录是站点常量，不随用户变化，客户端可长期缓存。",
    responses: {
      200: {
        description: "分组与条目清单；自定义条目的图片地址为 /api/emoji/:emojiId/image。",
        schema: EmojiCatalogSchema,
      },
    },
  },
  {
    operationId: "readEmojiImage",
    method: "GET",
    path: "/api/emoji/:emojiId/image",
    tags: ["表情"],
    implementationStatus: "implemented",
    summary: "读取自定义表情图片",
    description:
      "按表情 id 返回内置 PNG 二进制并设置 immutable 缓存头；id 必须存在于站点表情目录且自带图片资源，否则返回 404。",
    params: EmojiImageParamsSchema,
    responses: {
      200: { description: "PNG 图片二进制。", schema: z.any() },
      404: { description: "表情不存在或没有图片资源。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createDiceRoll",
    method: "POST",
    path: "/api/dice",
    tags: ["骰子"],
    summary: "创建稳定骰子结果",
    description: "表达式由 RPG Dice Roller 解析，例如 3d5。服务端只投掷一次并持久化。",
    body: CreateDiceRollRequestSchema,
    responses: {
      201: { description: "新 rollId、明细和总数，例如 3d5=12。", schema: DiceRollSchema },
      422: { description: "骰子表达式不可解析。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getDiceRoll",
    method: "GET",
    path: "/api/dice/:rollId",
    tags: ["骰子"],
    summary: "读取稳定骰子结果",
    description: "重复读取同一 rollId 绝不会重新投掷。",
    params: diceParams,
    responses: {
      200: { description: "已持久化的原始结果。", schema: DiceRollSchema },
      404: { description: "骰子结果不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "rerollDice",
    method: "POST",
    path: "/api/dice/:rollId/reroll",
    tags: ["骰子"],
    summary: "显式重新投掷",
    description: "创建新 rollId，并通过 previousRollId/rootRollId 保留重投链。",
    params: diceParams,
    responses: {
      201: { description: "新投掷结果。", schema: DiceRollSchema },
      404: { description: "原骰子结果不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getCommentThread",
    method: "GET",
    path: "/api/documents/:documentId/comments/:anchorId",
    tags: ["间贴"],
    summary: "读取间贴回复树",
    description: "只对根回复做游标分页，返回根节点的完整后代；sort=score 或 newest。",
    params: threadParams,
    query: commentQuery,
    responses: {
      200: { description: "锚点的树状回复及当前身份投票。", schema: CommentThreadSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createCommentReply",
    method: "POST",
    path: "/api/documents/:documentId/comments/:anchorId/replies",
    tags: ["间贴"],
    summary: "新增间贴回复",
    description: "所有论坛身份可回复。parentId 为 null 创建根回复，否则创建楼中楼。",
    params: threadParams,
    body: CreateCommentReplyRequestSchema,
    responses: {
      201: { description: "新回复；children 初始为空。", schema: CommentReplySchema },
      404: { description: "文档或父回复不存在。", schema: ApiErrorSchema },
      409: { description: "锚点已随正文删除并归档。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "voteComment",
    method: "PUT",
    path: "/api/comments/replies/:replyId/vote",
    tags: ["间贴"],
    summary: "赞、踩或撤销",
    description: "value=1 点赞、-1 点踩、0 撤销；每个身份对每条回复只有一票。",
    params: replyParams,
    body: VoteCommentRequestSchema,
    responses: {
      200: {
        description: "更新后的净赞数、赞踩计数和当前投票。",
        schema: z
          .object({
            score: z.number().int(),
            viewerVote: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
            upvotes: z.number().int().nonnegative(),
            downvotes: z.number().int().nonnegative(),
            myVote: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
          })
          .strict(),
      },
      404: { description: "回复不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getForumSession",
    method: "GET",
    path: "/api/forum/session",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取当前论坛身份",
    description:
      "生产环境从 HttpOnly session cookie 读取当前身份；本地 demo 模式可使用 x-user-id 切换种子身份。",
    responses: { 200: { description: "当前身份和可切换身份。", schema: ForumSessionSchema } },
  },
  {
    operationId: "listChapters",
    method: "GET",
    path: "/api/forum/chapters",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取章节目录",
    description: "仅登录用户返回指定文档按 order 排序的章节目录，非编辑者看不到隐藏章节。",
    query: documentQuery,
    responses: {
      200: {
        description: "按 order 升序的章节。",
        schema: z.object({ items: z.array(ChapterSchema) }).strict(),
      },
    },
  },
  {
    operationId: "listSuggestions",
    method: "GET",
    path: "/api/forum/documents/:documentId/suggestions",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取纠错建议",
    description: "作者与版主可看到全部状态；读者仅看到自己提交的建议。",
    params: documentParams,
    responses: {
      200: {
        description: "纠错建议列表。",
        schema: z.object({ items: z.array(SuggestionSchema) }).strict(),
      },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createSuggestion",
    method: "POST",
    path: "/api/forum/documents/:documentId/suggestions",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "提交纠错建议",
    description:
      "读者提交待审核文字替换；未审核内容不会写入正文。chapterId 使用服务端章节目录身份；lineNo 为章内顶层块行号（从 1 开始，0 表示未知），lineText 为该行完整文本，chapterTitle 仅供展示。",
    params: documentParams,
    body: CreateSuggestionRequestSchema,
    responses: {
      201: { description: "pending 状态建议。", schema: SuggestionSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "reviewSuggestion",
    method: "PATCH",
    path: "/api/forum/suggestions/:suggestionId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "审核纠错建议",
    description:
      "仅 author/moderator。approve 按服务端 documentId + chapterId 身份限定章节，核对 lineNo 与完整 lineText；行号漂移时仅接受章内唯一上下文，行内 fromText 也必须唯一。并发基线是提交建议时目标章节的内容版本：其他章节的更新不会让本建议失效，目标章节自身变化时返回 409 CHAPTER_REVISION_CONFLICT 且不写任何正文。无定位旧建议仅允许全文唯一匹配；已失效的章节或行定位不降级为全文首次替换。独立章节正文与文档快照不一致时拒绝应用。成功创建 operation=suggestion 的真实修订；找不到原文或上下文返回 409 SUGGESTION_SOURCE_NOT_FOUND，定位歧义返回 409 SUGGESTION_SOURCE_AMBIGUOUS，均不改变正文和建议状态。reject 只更新建议状态。",
    params: suggestionParams,
    body: ReviewSuggestionRequestSchema,
    responses: {
      200: {
        description: "审核后的建议和可选新文档修订。",
        schema: z
          .object({ suggestion: SuggestionSchema, document: DocumentEnvelopeSchema.nullable() })
          .strict(),
      },
      403: { description: "当前身份不可审核。", schema: ApiErrorSchema },
      404: { description: "建议不存在。", schema: ApiErrorSchema },
      409: {
        description:
          "建议已审核、目标章节内容已被其他修改更新（CHAPTER_REVISION_CONFLICT）、" +
          "或原文/章节/行定位不存在或有歧义。其他章节的更新不影响本建议。",
        schema: ApiErrorSchema,
      },
    },
  },
  {
    operationId: "listSuggestionBatches",
    method: "GET",
    path: "/api/forum/documents/:documentId/suggestion-batches",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取整章批量校订",
    description: "作者与版主可查看全部批次；读者仅看到自己提交的批次。",
    params: documentParams,
    responses: {
      200: {
        description: "批量校订列表。",
        schema: z.object({ items: z.array(SuggestionBatchSchema) }).strict(),
      },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createSuggestionBatch",
    method: "POST",
    path: "/api/forum/documents/:documentId/suggestion-batches",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "提交整章批量校订",
    description: "把整章编辑产生的多个 ProseMirror steps 合并为一个待审核批次。",
    params: documentParams,
    body: CreateSuggestionBatchRequestSchema,
    responses: {
      201: { description: "pending 状态批次。", schema: SuggestionBatchSchema },
      409: { description: "提交基线已过期。", schema: ApiErrorSchema },
      422: { description: "steps 与编辑后快照不一致。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "reviewSuggestionBatch",
    method: "PATCH",
    path: "/api/forum/suggestion-batches/:batchId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "原子审核整章批量校订",
    description:
      "接受时基于最新正文安全重放当前章节的全部修改并只创建一个 revision；其他章节变化不会阻塞，当前章节变化返回冲突；拒绝不修改正文。",
    params: suggestionBatchParams,
    body: ReviewSuggestionBatchRequestSchema,
    responses: {
      200: {
        description: "审核后的批次和可选新文档修订。",
        schema: z
          .object({ batch: SuggestionBatchSchema, document: DocumentEnvelopeSchema.nullable() })
          .strict(),
      },
      403: { description: "当前身份不可审核。", schema: ApiErrorSchema },
      409: { description: "批次已审核或基线过期。", schema: ApiErrorSchema },
      422: { description: "steps 无法应用。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "searchMentionUsers",
    method: "GET",
    path: "/api/forum/users/search",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "搜索 @ 用户",
    description: "q 同时匹配名称和 ID；friendsOnly=true 仅返回当前身份好友。",
    query: userSearchQuery,
    responses: { 200: { description: "最多 20 个候选用户。", schema: MentionSearchResultSchema } },
  },
  {
    operationId: "resolveMention",
    method: "POST",
    path: "/api/forum/mentions/resolve",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "服务端解析 @",
    description: "发送时按可选 ID 或精确名称解析非好友；失败时客户端应渲染普通文本。",
    body: ResolveMentionRequestSchema,
    responses: {
      200: {
        description: "resolved 表示是否使用 @ 成功样式。",
        schema: ResolveMentionResponseSchema,
      },
    },
  },
  {
    operationId: "resolveReplyGate",
    method: "POST",
    path: "/api/forum/reply-gates/resolve",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "解析回复可见内容",
    description: "按章节回复凭证返回内容；文章作者始终可见，其他身份必须先回复对应章节。",
    body: ResolveReplyGateRequestSchema,
    responses: {
      200: { description: "不可见时 content 为 null。", schema: ResolveReplyGateResponseSchema },
      404: { description: "gateId 不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getAttachment",
    method: "GET",
    path: "/api/forum/attachments/:attachmentId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取附件购买状态",
    description: "未购买时隐藏 downloadUrl；作者和版主直接具备权限。",
    params: attachmentParams,
    responses: {
      200: { description: "附件价格和当前身份权益。", schema: AttachmentSchema },
      404: { description: "附件不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "purchaseAttachment",
    method: "POST",
    path: "/api/forum/attachments/:attachmentId/purchase",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "购买附件",
    description: "金币账务；重复购买幂等，首次购买扣款并按售价 70% 向作者入账。",
    params: attachmentParams,
    responses: {
      200: { description: "已购买或重复购买结果。", schema: PurchaseAttachmentResponseSchema },
      402: { description: "金币不足。", schema: ApiErrorSchema },
      404: { description: "附件不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "getPoll",
    method: "GET",
    path: "/api/forum/polls/:pollId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取投票统计",
    description: "返回当前身份资格、已选选项和实时计数。",
    params: pollParams,
    responses: {
      200: { description: "投票详情。", schema: PollSchema },
      404: { description: "投票不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "submitPollVote",
    method: "POST",
    path: "/api/forum/polls/:pollId/votes",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "提交或改投",
    description: "满足资格的用户可投；单选投票只能提交一个 optionId，重复提交会更新选择。",
    params: pollParams,
    body: SubmitPollVoteRequestSchema,
    responses: {
      200: { description: "更新后的投票详情。", schema: PollSchema },
      403: { description: "当前身份不满足投票要求。", schema: ApiErrorSchema },
      404: { description: "投票或选项不存在。", schema: ApiErrorSchema },
      422: { description: "选项数量不符合单选/多选要求。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "listPollVotes",
    method: "GET",
    path: "/api/forum/polls/:pollId/votes",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "分页读取实名投票",
    description: "按投票时间倒序，cursor 为上一页最后一条记录 ID。",
    params: pollParams,
    query: CursorQuerySchema,
    responses: {
      200: { description: "实名用户与所选 optionId。", schema: PollVotePageSchema },
      404: { description: "投票不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "listDocumentTags",
    method: "GET",
    path: "/api/documents/:documentId/tags",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "读取文章标签",
    description:
      "标签属于整篇文章，与章节无关。编辑器额外看到已隐藏的服务器标签；读者只看到可展示标签。",
    params: documentParams,
    responses: {
      200: { description: "按展示顺序排列的标签。", schema: DocumentTagsSchema },
      401: { description: "游客不能读取文章标签。", schema: ApiErrorSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "updateDocumentTags",
    method: "PUT",
    path: "/api/documents/:documentId/tags",
    tags: ["文档"],
    implementationStatus: "implemented",
    summary: "全量替换文章标签",
    description:
      "需要 author 或 moderator。只发送标签文本：命中未隐藏的站点标签时归为服务器标签，否则建成只属于这篇文章的作者标签（不会写进站点字典）。标签是元数据，不产生新修订、不改变 current_revision。",
    params: documentParams,
    body: UpdateDocumentTagsRequestSchema,
    responses: {
      200: { description: "替换后的标签集合。", schema: DocumentTagsSchema },
      401: { description: "需要登录。", schema: ApiErrorSchema },
      403: { description: "当前身份无编辑权限。", schema: ApiErrorSchema },
      404: { description: "文档不存在。", schema: ApiErrorSchema },
      422: {
        description: "标签文本非法（TAG_LABEL_INVALID）或超过 5 个（TAG_LIMIT_EXCEEDED）。",
        schema: ApiErrorSchema,
      },
    },
  },
  {
    operationId: "listServerTags",
    method: "GET",
    path: "/api/forum/tags",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "读取站点标签字典",
    description: "可选标签来源；默认不返回已隐藏条目。",
    responses: {
      200: { description: "按展示名排序的站点标签。", schema: ServerTagListSchema },
      401: { description: "需要登录。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "createServerTag",
    method: "POST",
    path: "/api/forum/tags",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "新建站点标签",
    description: "仅版主可用。slug 由标签文本推导且之后不可修改；同 slug 视为重复，返回 409。",
    body: CreateTagRequestSchema,
    responses: {
      201: { description: "新建的站点标签。", schema: TagSchema },
      401: { description: "需要登录。", schema: ApiErrorSchema },
      403: { description: "只有版主可以维护站点字典。", schema: ApiErrorSchema },
      409: { description: "同名或同 slug 的标签已存在。", schema: ApiErrorSchema },
      422: { description: "标签文本非法。", schema: ApiErrorSchema },
    },
  },
  {
    operationId: "updateServerTag",
    method: "PATCH",
    path: "/api/forum/tags/:tagId",
    tags: ["论坛业务"],
    implementationStatus: "implemented",
    summary: "维护站点标签",
    description:
      "仅版主可用。slug 不可修改；改 label 会同步所有引用该标签的文章展示文本，hidden 只做软隐藏。",
    params: tagParams,
    body: UpdateTagRequestSchema,
    responses: {
      200: { description: "更新后的站点标签。", schema: TagSchema },
      401: { description: "需要登录。", schema: ApiErrorSchema },
      403: { description: "只有版主可以维护站点字典。", schema: ApiErrorSchema },
      404: { description: "标签不存在。", schema: ApiErrorSchema },
      409: { description: "改名后与既有标签冲突。", schema: ApiErrorSchema },
      422: { description: "标签文本非法或没有提供任何字段。", schema: ApiErrorSchema },
    },
  },
];

/** 按 operationId 获取路由契约；未找到时抛出开发期错误。 */
export function getContractRoute(operationId: string): ContractRoute {
  const route = contractRoutes.find((candidate) => candidate.operationId === operationId);
  if (!route) throw new Error(`未知契约 operationId: ${operationId}`);
  return route;
}
